import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects } from '../api/queries/projects';
import { useIntakeEvidence, type IntakeEvidenceInput } from '../api/queries/evidence';
import { ApiError } from '../api/client';
import { DateInput } from '../components/DateInput';
import { LookupSelect } from '../components/LookupSelect';
import { ProjectOptions } from '../components/ProjectOptions';
import type { EvidenceType } from '@stfors/shared-types';
import './NewEvidencePage.css';

function nowEpoch(): number {
  return Date.now();
}

export function NewEvidencePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intake = useIntakeEvidence();
  const { data: projectsData, isPending: projectsLoading, error: projectsError } = useProjects();

  // ?project_id= prefill (e.g. when intake is launched from a project page)
  const initialProjectId = (() => {
    const v = searchParams.get('project_id');
    if (!v) return 0;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : 0;
  })();

  const [projectId, setProjectId] = useState<number>(initialProjectId);
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('mobile_device');
  const [description, setDescription] = useState('');
  const [acquiredAt, setAcquiredAt] = useState<number | null>(() => nowEpoch());
  const [serialNumber, setSerialNumber] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [clientName, setClientName] = useState('');
  const [collectedFrom, setCollectedFrom] = useState('');
  const [storagePhysical, setStoragePhysical] = useState('');
  const [storageDigital, setStorageDigital] = useState('');

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (projectsLoading) return <p className="ne-status">Loading projects…</p>;
  if (projectsError) {
    return <p className="ne-status ne-error">Could not load the projects list.</p>;
  }
  const projects = projectsData?.projects ?? [];
  if (projects.length === 0) {
    return (
      <div className="ne-noprojects">
        <h2>No projects yet</h2>
        <p>Evidence belongs to a project. Create one first, then come back.</p>
        <Link to="/projects/new" className="btn-cyan-primary">Create a project</Link>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (projectId <= 0) {
      setFieldErrors({ project_id: 'Select a project' });
      return;
    }
    if (acquiredAt === null) {
      setFieldErrors({ acquired_date: 'Pick an acquisition timestamp' });
      return;
    }

    const payload: IntakeEvidenceInput = {
      project_id: projectId,
      evidence_type: evidenceType,
      description: description.trim(),
      acquired_date: acquiredAt,
      serial_number: serialNumber.trim() || undefined,
      make_model: makeModel.trim() || undefined,
      owner_name: ownerName.trim() || undefined,
      client_name: clientName.trim() || undefined,
      collected_from: collectedFrom.trim() || undefined,
      storage_physical: storagePhysical.trim() || undefined,
      storage_digital: storageDigital.trim() || undefined,
    };

    try {
      await intake.mutateAsync(payload);
      navigate('/evidence');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to intake evidence on this project.");
        return;
      }
      setTopError('Could not record the evidence intake. Please try again.');
    }
  };

  return (
    <section className="ne-section">
      <header className="ne-header">
        <h2>Evidence Intake</h2>
        <button
          type="button"
          className="btn-text"
          onClick={() => navigate('/evidence')}
        >
          ← Back to evidence
        </button>
      </header>

      <form className="ne-form" onSubmit={onSubmit} noValidate>
        <div className="ne-row">
          <label className="ne-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(Number(e.target.value))}
              required
            >
              <option value={0}>Select a project…</option>
              <ProjectOptions projects={projects} />
            </select>
            {fieldErrors['project_id'] && (
              <span className="field-error">{fieldErrors['project_id']}</span>
            )}
          </label>

          <label className="ne-field">
            <span>Evidence type *</span>
            <LookupSelect
              category="evidence_type"
              value={evidenceType}
              onChange={(v) => setEvidenceType(v as EvidenceType)}
              className="form-input"
            />
          </label>
        </div>

        <label className="ne-field ne-field-full">
          <span>Description *</span>
          <textarea
            className="form-input ne-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            required
            maxLength={10000}
            placeholder="Subject's iPhone 14 Pro, black, with cracked screen"
          />
          {fieldErrors['description'] && (
            <span className="field-error">{fieldErrors['description']}</span>
          )}
        </label>

        <div className="ne-row">
          <label className="ne-field">
            <span>Acquired at *</span>
            <DateInput
              value={acquiredAt}
              onChange={setAcquiredAt}
              withTime
              required
            />
          </label>

          <label className="ne-field">
            <span>Collected from</span>
            <input
              type="text"
              className="form-input"
              value={collectedFrom}
              onChange={(e) => setCollectedFrom(e.target.value)}
              maxLength={500}
              placeholder="123 Main St / scene reference"
            />
          </label>
        </div>

        <div className="ne-row">
          <label className="ne-field">
            <span>Make / model</span>
            <input
              type="text"
              className="form-input"
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              maxLength={200}
            />
          </label>

          <label className="ne-field">
            <span>Serial number</span>
            <input
              type="text"
              className="form-input"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              maxLength={200}
            />
          </label>
        </div>

        <div className="ne-row">
          <label className="ne-field">
            <span>Owner name</span>
            <input
              type="text"
              className="form-input"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              maxLength={200}
            />
          </label>

          <label className="ne-field">
            <span>Client name</span>
            <input
              type="text"
              className="form-input"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              maxLength={200}
            />
          </label>
        </div>

        <div className="ne-row">
          <label className="ne-field">
            <span>Physical storage</span>
            <input
              type="text"
              className="form-input"
              value={storagePhysical}
              onChange={(e) => setStoragePhysical(e.target.value)}
              maxLength={500}
              placeholder="Locker 4, shelf B"
            />
          </label>

          <label className="ne-field">
            <span>Digital storage path</span>
            <input
              type="text"
              className="form-input"
              value={storageDigital}
              onChange={(e) => setStorageDigital(e.target.value)}
              maxLength={1000}
              placeholder="vault/proj-12/coc-{generated}/image.dd"
            />
          </label>
        </div>

        {topError && (
          <p className="ne-error-banner" role="alert">
            {topError}
          </p>
        )}

        <p className="ne-hint">
          Hash will be set later via the <code>imaged</code> event when forensic
          imaging completes — do not enter it manually here.
        </p>

        <div className="ne-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/evidence')}
            disabled={intake.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={
              intake.isPending ||
              projectId <= 0 ||
              description.trim().length === 0
            }
          >
            {intake.isPending ? 'Recording intake…' : 'Record intake'}
          </button>
        </div>
      </form>
    </section>
  );
}
